/**
 * ArenaRenderer — draws the map the simulation is actually using.
 *
 * The geometry here comes from the same `MapDef` the predictor collides against,
 * so what you see is what you stand on. v1 drew a decorative 200x200 ground quad
 * and let the AI spawn entities near it; the client's idea of the floor and the
 * server's were related only by convention.
 *
 * It also owns the presentation of MARKET CONDITIONS on the arena: the floor
 * overlays (ice / conveyor / lava) it builds hidden with the map, and — through
 * `MarketFx` — the fog roll, the tremor, the colour shift and the low-gravity
 * motes, all read off one chaos ledger every frame. And it PREWARMS every one
 * of those materials the moment the map is built, against the real render
 * target, because the first draw of an uncompiled overlay mid-fight cost a
 * 480 ms frame (docs/design/vfx3/MARKET.md).
 */

import * as THREE from 'three/webgpu';
import {
  createSurfaceMaterial,
  panelRepeatFor,
  setMaterialTheme,
  materialThemeColorShift,
  clearMaterialCache,
} from './ToonMaterials.js';
import {
  createLavaShaderMaterial,
  createDeckMaterial,
  createPlatformRimMaterial,
  clearPlatformRimMaterials,
  clearConveyorMaterials,
  getChaosOverlayMaterials,
  overlayErodeValue,
  registerSurfaceFrameHook,
} from './SurfaceShaders.js';
import {
  initInstancedBatchManager,
  isInstanceable,
  addInstancedPlatform,
  clearAllBatches,
} from './entities/InstancedBatchManager.js';
import {
  initParticles,
  updateSkyColors,
  selectParticleType,
  layoutFogClouds,
  fogCloudMeshes,
  setFogRoll,
  fogRollState,
  setAmbientGravity,
} from './EnvironmentEffects.js';
import { setTremor, tremorJolt, acquireBurstPrewarm, releaseBurstPrewarm } from './vfx/ScreenEffects.js';
import { setParticleFade } from './vfx/ParticleUtils.js';
import { setColorShift, scenePassTarget } from './PostProcessing.js';
import { isProp } from './props/TradingFloorProps.js';
import { buildPitVisual, buildPitFallback, buildMutationGeometry } from './arenas/pit-visual.js';
import { buildChartVisual, buildChartFallback } from './arenas/chart-visual.js';
import { beginProps, addProp, flushProps, clearProps } from './props/PropBatcher.js';
import { getMapMeta } from './maps/index.js';
import { getLocalVisual } from './arenas/local-visuals.js';
import { buildHftVisual } from './arenas/hft-visual.js';
import { mergeStaticArena } from './arenas/mergeStatic.js';
import { fetchArenaAsset } from './arenas/arenaBinary.js';
import { chaosWorld, setChaosWorldSource } from './arenas/market-state.js';
import { MarketFx } from './arenas/MarketFx.js';

// The arena mockup tool and the local-visual test import the source setter
// from here; the reader itself lives in `arenas/market-state.js` now so the
// banner and the fog can read the same ledger without importing the renderer.
export { setChaosWorldSource };

/**
 * How far BELOW `floor.y` the lava plane is drawn.
 *
 * Lava is the one floor kind still drawn as decoration over other geometry, so it
 * is the one that needs the dip: coplanar surfaces leave the depth buffer nothing
 * to order by and the ground reads as horizontal striping — worse on the WebGL2
 * fallback, whose depth buffer is coarser. The solid deck no longer needs it,
 * because the square collision slab it used to sit over is gone; that deck IS the
 * collision surface now and takes a smaller lift of its own (see `_buildFloor`).
 *
 * 0.05 rather than 0.001: the camera is near 0.1 / far 1200, which leaves roughly
 * 0.01 units of depth resolution at the far edge of the arena, and the step is
 * invisible at a scale where a player is two units tall.
 */
const FLOOR_DEPTH_EPSILON = 0.05;

/** The three overlay kinds, every one compiled at map load. */
const OVERLAY_KINDS = ['ice', 'conveyor', 'lava'];

/**
 * The warm draw's scale: the vertex shader runs, no fragment is shaded.
 *
 * `compileAsync` creates the GPU pipelines, but in three r183 it can never
 * produce a node-builder state a scene PASS will reuse: `_renderScene` keys
 * its render context on the renderer's call depth, a pass renders one level
 * deep, and `compileAsync` always asks for depth zero — so the first real
 * draw rebuilt the TSL graph (60–115 ms of JS for the lava) even with the
 * pipeline cached. After the pipelines exist, the real overlay meshes are
 * therefore drawn once through the real pass, one material per frame, at
 * this scale, and that build lands at map load instead of mid-fight.
 */
const WARM_SCALE = 1e-3;

/** The warm draw waits at most this long for pending pipelines. */
const WARM_WAIT_MS = 4000;

/**
 * True when every render pipeline the renderer has cached has its GPU object.
 *
 * A pipeline created by `compileAsync` (ours, or the combat kit's warmup
 * running through the drop) has no GPU object until Dawn finishes, and three
 * r183 draws it anyway: `setPipeline(undefined)` throws inside the animation
 * loop and the frame — every frame after it — comes out black. A real draw
 * of anything sharing such a pipeline's key must wait for it. Private-API
 * probe, guarded: anything unexpected reads as "ready" and we are no worse
 * than before.
 */
export function pipelinesSettled(renderer) {
  const caches = renderer?._pipelines?.caches;
  const backend = renderer?.backend;
  if (!caches || !backend?.get || backend.isWebGPUBackend !== true) return true;
  for (const pipeline of caches.values()) {
    if (pipeline?.isComputePipeline) continue;
    const data = backend.get(pipeline);
    if (data && data.pipeline === undefined && data.error !== true) return false;
  }
  return true;
}

const easeOut = (t) => 1 - (1 - t) * (1 - t);

export class ArenaRenderer {
  /**
   * @param {THREE.Scene} scene
   * @param {{ renderer?: THREE.WebGPURenderer, camera?: THREE.Camera }} [opts]
   *   The renderer and camera enable the prewarm. Tools that only lay out a map
   *   (the arena mockup, the attract backdrop, the portrait renderer) pass
   *   nothing and skip it.
   */
  constructor(scene, { renderer = null, camera = null } = {}) {
    this.scene = scene;
    this.root = new THREE.Group();
    this.root.name = 'arena';
    scene.add(this.root);
    initInstancedBatchManager(scene);
    this._renderer = renderer;
    this._camera = camera;
    this._floorMesh = null;
    this._chaosGround = null;
    this._chaosQuads = [];
    this._chaosKind = null;
    this._overlayRadius = 45;
    this._localVisualController = null;
    /** Resolves when the last build's prewarm has settled. */
    this.prewarmed = Promise.resolve({ compiled: 0 });
    /** Overlay kinds still to be drawn once through the real pass. */
    this._warmQueue = null;
    this._warmActive = false;
    this._warmBursts = [];
    /** Arena meshes drawn unculled for the warm frames; culling comes back after. */
    this._warmUnculled = [];
    this.market = new MarketFx({
      fog: setFogRoll,
      shift: setColorShift,
      tremor: setTremor,
      jolt: tremorJolt,
      gravity: setAmbientGravity,
    });
    registerSurfaceFrameHook((delta) => this._tickChaos(delta));
  }

  /** Builds every mesh for one map. Called once per match. */
  build(map) {
    this.clear();
    const meta = getMapMeta(map.id);
    setMaterialTheme(meta.theme);

    const localVisual = getLocalVisual(map.id);
    if (localVisual) {
      const controller = localVisual(this.root, map);
      if (controller?.update || controller?.dispose) this._localVisualController = controller;
      this._finishBuild(map, meta);
      return;
    }

    if (map.visual) {
      const isChart = map.visual.builder === 'chart' || map.visual.builder === 'hft';
      const label = map.name;
      const buildVisual = map.visual.builder === 'hft' ? buildHftVisual : isChart ? buildChartVisual : buildPitVisual;
      const art = new THREE.Group();
      this._assetRoot = art;
      this.root.add(art);
      (isChart ? buildChartFallback : buildPitFallback)(art, map);
      const status = document.createElement('div');
      status.setAttribute('role', 'status');
      status.style.cssText = 'position:fixed;bottom:8px;left:8px;background:#241c16;color:#fff;padding:6px 10px;z-index:100;pointer-events:none;font:12px sans-serif';
      status.textContent = `Loading ${label} artwork…`;
      document.body.append(status);
      this._assetStatus = status;
      this.assetState = 'loading';
      this.ready = fetchArenaAsset(map.visual.asset).then(asset => {
        if (this._assetRoot !== art) return;
        if (asset.version !== 1 || !asset.batches?.length || asset.batches.some(b => !b.partId || !b.positions?.length || b.normals?.length !== b.positions.length)) throw new Error(`Invalid ${label} geometry`);
        // Build before discarding the playable fallback, so a malformed mesh
        // cannot leave a blank arena. Clearing the map invalidates this load.
        const ready = new THREE.Group();
        try {
          const controller = buildVisual(ready, map, asset);
          if (controller?.update || controller?.dispose) this._localVisualController = controller;
          // After the controller has tagged what it drives: one draw per look,
          // not one per exported part. See mergeStaticArena.
          mergeStaticArena(ready);
        }
        catch (error) { ready.traverse(o => { o.geometry?.dispose(); o.material?.dispose(); }); throw error; }
        art.traverse(o => { o.geometry?.dispose(); o.material?.dispose(); });
        art.clear();
        art.add(...ready.children.slice());
        this.assetState = 'ready';
        status.remove();
      }).catch(error => {
        if (this._assetRoot !== art) return;
        this.assetState = 'fallback';
        status.textContent = `${label} artwork unavailable — showing playable collision geometry`;
        console.error(`${label} artwork unavailable`, error);
      });
      this._finishBuild(map, meta);
      return;
    }

    this._buildFloor(map);

    beginProps();
    for (const platform of map.platforms) {
      // A prop REPLACES the box, it does not decorate one: drawing both would
      // leave a grey slab sticking out of every desk. The platform still exists
      // for the sim, which is the point — art changed, collision did not.
      //
      // Queued rather than added: a furnished arena holds a few hundred props
      // and drawing each one as its own Group cost 2,200 draw calls. See
      // PropBatcher — they all land as a handful of InstancedMeshes below.
      if (platform.model && isProp(platform.model)) {
        // Props are authored with their base at y=0; the platform is stored by
        // its centre, so the base is one half-height below it.
        //
        // The rotation is visual only. Collision is an AABB, so a map may not
        // rotate a prop off the axes without the box and the model disagreeing
        // — quarter turns are the only safe values and all any arena uses.
        addProp(platform.model, platform.x, platform.y - platform.hy, platform.z, platform.rotY ?? 0);
        continue;
      }

      if (isInstanceable(platform)) {
        addInstancedPlatform(platform);
      } else {
        this.root.add(this._buildPlatformMesh(platform));
      }
      const rim = this._buildPlatformRim(platform, map.floor);
      if (rim) this.root.add(rim);
    }

    flushProps(this.root);

    for (const zone of map.windZones) this.root.add(this._buildWindZone(zone));

    this._finishBuild(map, meta);
  }

  /** The part of a build every branch shares: overlays, sky, weather, prewarm. */
  _finishBuild(map, meta) {
    this._buildChaosOverlays(map);
    updateSkyColors(null, null, meta.sky);
    initParticles(this.scene, selectParticleType(map.floor.kind, { skyPreset: meta.sky }));
    // The clouds sit on the same ground the overlays do — on a mesh deck that
    // is the platform tops, not the sim's nominal floor height.
    layoutFogClouds(map, this._groundY);
    this.market.reset();
    const built = this._chaosGround;
    // SERIALISED. The gym builds twice in a row (its boot map, then the one
    // asked for), and the pipeline cache is shared: a second `compileAsync`
    // hits the entries the first one created and resolves at once, while the
    // first one's GPU pipelines are still being created — and a warm draw on
    // a pipeline with no GPU object yet throws inside the render loop.
    const previous = this._prewarmTail ?? Promise.resolve();
    const run = previous.then(() => this.prewarm(), () => this.prewarm());
    this._prewarmTail = run;
    // The warm frames also wait for the map's ART: on the mesh decks the
    // outline pass builds one node state per arena mesh the first time it
    // enters the frustum, and the warm frames draw the whole arena unculled
    // so those land here too (see `_warmDraw`).
    this.prewarmed = Promise.all([run, this.ready]).then(([result]) => {
      // The pipelines exist; now the node builds, through the real pass.
      if (result.compiled > 0 && this._chaosGround === built) this._warmQueue = OVERLAY_KINDS.slice();
      return result;
    });
  }

  _buildFloor(map) {
    const floor = map.floor;
    if (floor.kind === 'none') return;

    if (floor.kind === 'lava') {
      // Sized to the arena rather than a fixed 200: the fog and the zone wall
      // are what define the edge of the world, and a floor that outruns both
      // reads as an empty plain.
      const size = map.startRadius * 2.6;
      const geo = new THREE.PlaneGeometry(size, size, 24, 24);
      const mesh = new THREE.Mesh(geo, createLavaShaderMaterial());
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(map.centerX, floor.y - FLOOR_DEPTH_EPSILON, map.centerZ);
      mesh.userData.ownsMaterial = true;
      this.root.add(mesh);
      this._floorMesh = mesh;
      return;
    }

    // Drawn at `floorRadius`, which is exactly where `onArenaFloor` says there
    // is ground — this circle IS the collision surface, not a decoration over
    // one. The map used to carry a square collision slab underneath as well,
    // and its corners stuck out past the drawn circle, so the honest edge and
    // the standable edge disagreed; the slab is gone and this is the only
    // answer to "where can I stand". Lifted 0.02 rather than the full epsilon:
    // at 0.05 the deck visibly buried character feet.
    const geo = new THREE.CircleGeometry(map.floorRadius, 96);
    const mesh = new THREE.Mesh(
      geo,
      createDeckMaterial({
        arenaRadius: map.startRadius,
        centerX: map.centerX,
        centerZ: map.centerZ,
        colorShift: materialThemeColorShift(),
      })
    );
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(map.centerX, floor.y + 0.02, map.centerZ);
    mesh.receiveShadow = true;
    mesh.userData.ownsMaterial = true;
    this.root.add(mesh);
    this._floorMesh = mesh;
  }

  _buildPlatformMesh(platform) {
    const geo = new THREE.BoxGeometry(platform.hx * 2, platform.hy * 2, platform.hz * 2);
    const mesh = new THREE.Mesh(
      geo,
      // Same tiling rule as the instanced path: whether a platform is instanced
      // is a draw-call decision and must not change how its surface looks.
      createSurfaceMaterial(platform.surface, panelRepeatFor(Math.max(platform.hx, platform.hz) * 2))
    );
    mesh.position.set(platform.x, platform.y, platform.z);
    mesh.castShadow = platform.solid;
    mesh.receiveShadow = true;
    mesh.name = platform.id;
    // Conveyors are the one surface built per platform rather than pulled from
    // the shared cache, so they are the one whose material this mesh owns —
    // including its cloned scrolling texture (`ownsMap`). The normal map stays
    // shared and must never be disposed here.
    mesh.userData.ownsMaterial = platform.surface.kind === 'conveyor';
    mesh.userData.ownsMap = platform.surface.kind === 'conveyor';
    return mesh;
  }

  /**
   * The luminous edge line around a platform's top face — the cue you judge a
   * jump against. Skipped for decking at floor level (the arena deck already
   * owns the ground read) and for anything a player cannot stand on.
   */
  _buildPlatformRim(platform, floor) {
    if (!platform.solid) return null;
    const top = platform.y + platform.hy;
    const floorY = floor.kind === 'none' ? -Infinity : floor.y;
    if (top <= floorY + 0.25) return null;

    const geo = new THREE.PlaneGeometry(platform.hx * 2, platform.hz * 2);
    const mesh = new THREE.Mesh(geo, createPlatformRimMaterial(platform.hx, platform.hz));
    mesh.rotation.x = -Math.PI / 2;
    mesh.position.set(platform.x, top + 0.02, platform.z);
    mesh.renderOrder = 1;
    // Material lives in the rim cache — clear() must not dispose it per-mesh.
    mesh.userData.ownsMaterial = false;
    return mesh;
  }

  /**
   * Hidden meshes for the floor mutators, one arena circle plus a quad per
   * plain platform — built with the arena so an activation mid-fight only
   * toggles visibility. The sim's rule (mutations.ts `chaosSurface`): a chaos
   * floor covers the arena floor and NORMAL/ICE platforms; bounce pads,
   * speed strips and authored conveyors keep their own mechanics, so they
   * keep their own look. Raised platforms are also deliberately excluded
   * from LAVA — they are the refuge, and painting them would say "lethal".
   */
  _buildChaosOverlays(map) {
    // Chart is a square mesh deck. Its corner panels still burn outside the
    // starting zone circle; fade only beyond the authored collision extents.
    let overlayRadius = map.startRadius;
    if (map.visual?.builder === 'chart' || map.visual?.builder === 'hft') {
      for (const p of map.platforms) {
        if (p.solid) overlayRadius = Math.max(overlayRadius, Math.hypot(Math.abs(p.x) + p.hx, Math.abs(p.z) + p.hz));
      }
    }
    const mats = getChaosOverlayMaterials();
    mats.radius.value = overlayRadius;
    mats.center.value.set(map.centerX, map.centerZ);
    this._overlayRadius = overlayRadius;
    this._chaosQuads = [];
    this._chaosGround = null;
    this._chaosKind = null;
    this._groundY = map.floor.kind === 'none' ? 0 : map.floor.y;

    if (map.floor.kind === 'solid') {
      const geo = new THREE.CircleGeometry(map.floorRadius, 96);
      const mesh = new THREE.Mesh(geo, mats.ice);
      mesh.rotation.x = -Math.PI / 2;
      mesh.position.set(map.centerX, map.floor.y + 0.032, map.centerZ);
      mesh.visible = false;
      // Arena-sized: culling never helps, and the warm draw needs it drawn.
      mesh.frustumCulled = false;
      mesh.userData.ownsMaterial = false;
      this.root.add(mesh);
      this._chaosGround = mesh;
    }

    const surfaces = buildMutationGeometry(map);
    for (const [kind, geo] of Object.entries(surfaces)) {
      const position = geo.getAttribute('position');
      if (!position.count) { geo.dispose(); continue; }
      const mesh = new THREE.Mesh(geo, mats.ice);
      mesh.visible = false;
      mesh.frustumCulled = false;
      mesh.userData.ownsMaterial = false;
      this.root.add(mesh);
      if (kind === 'ground') {
        this._chaosGround = mesh;
        // The highest ground-level platform top: where the deck actually is.
        let top = -Infinity;
        for (let i = 0; i < position.count; i++) top = Math.max(top, position.getY(i));
        if (Number.isFinite(top)) this._groundY = top;
      } else {
        this._chaosQuads.push(mesh);
      }
    }
  }

  /**
   * Compiles every market material this map can show, NOW, against the
   * render target the scene actually draws into.
   *
   * Three keys a WebGPU render pipeline on the render context's sample count
   * and colour format as well as the material, and on the post-processed
   * tiers the scene is drawn into the pass's HalfFloat target, not the canvas
   * — a prewarm against the canvas compiles a pipeline the first real draw
   * cannot use. `compileAsync` issues every pipeline synchronously before its
   * first await, so the target is bound around the call and restored at once.
   *
   * Every overlay material is compiled on every overlay geometry it can wear
   * (the ground disc and the raised quads have different vertex layouts, and
   * the layout is part of the key too), plus the fog cloud layers.
   *
   * @param {{ target?: THREE.RenderTarget|null }} [opts] the pass target; tests inject one
   */
  async prewarm({ target = scenePassTarget() } = {}) {
    const renderer = this._renderer;
    const camera = this._camera;
    if (!renderer?.compileAsync || !camera) return { compiled: 0, skipped: true };

    const isolated = new THREE.Scene();
    isolated.fog = this.scene.fog;
    const meshes = [];
    const stage = (geometry, material, like = null) => {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.frustumCulled = false;
      if (like) {
        mesh.position.copy(like.position);
        mesh.rotation.copy(like.rotation);
        mesh.scale.copy(like.scale);
      }
      isolated.add(mesh);
      meshes.push(mesh);
    };
    const mats = getChaosOverlayMaterials();
    for (const overlay of [this._chaosGround, ...this._chaosQuads]) {
      if (!overlay) continue;
      for (const kind of OVERLAY_KINDS) stage(overlay.geometry, mats[kind], overlay);
    }
    for (const cloud of fogCloudMeshes()) stage(cloud.geometry, cloud.material, cloud);
    // NOT the pooled burst materials. `compileAsync` leaves a pipeline with no
    // GPU object until Dawn finishes, and anything that draws a pipeline of
    // the same key in that window throws inside the render loop (black
    // frames from then on). The overlays and the clouds are drawn by nobody
    // but this file, behind `pipelinesSettled`; the burst pool is shared with
    // the combat kit and the gym soak, so it is warmed by the real draw only.
    if (!meshes.length) return { compiled: 0 };

    const previous = renderer.getRenderTarget?.() ?? null;
    let compiling;
    renderer.setRenderTarget?.(target);
    try {
      compiling = renderer.compileAsync(isolated, camera, this.scene);
    } finally {
      renderer.setRenderTarget?.(previous);
    }
    try {
      await compiling;
    } catch (error) {
      console.warn('[arena] market prewarm failed; first activation will compile live', error);
    }
    for (const mesh of meshes) isolated.remove(mesh);
    return { compiled: meshes.length, target: target ?? null };
  }

  /**
   * Runs every render frame off SurfaceShaders' hook. Reads the chaos ledger
   * through MarketFx (which owns every transition clock) and drives the
   * shared overlay materials: which kind shows where, the conveyor's
   * direction, the arrival wipe and the removal erosion.
   */
  _tickChaos(delta) {
    const world = chaosWorld();
    this._localVisualController?.update?.(world);
    const fx = this.market.update(delta, world);
    if (!this._chaosGround && this._chaosQuads.length === 0) return;
    const mats = getChaosOverlayMaterials();
    this._warmRestore();
    this._applyFloor(fx.floor, mats);
    this._warmDraw(mats);
  }

  /** Puts everything the warm draw touched back, before the frame's real state. */
  _warmRestore() {
    if (!this._warmActive) return;
    this._warmActive = false;
    for (const mesh of [this._chaosGround, ...this._chaosQuads]) {
      if (!mesh) continue;
      mesh.scale.setScalar(1);
      mesh.visible = false;
    }
    // The material the market wants is re-applied by `_applyFloor`.
    this._chaosKind = null;
    for (const cloud of fogCloudMeshes()) {
      if (cloud.userData.warmScale !== undefined) {
        cloud.scale.setScalar(cloud.userData.warmScale);
        delete cloud.userData.warmScale;
      }
    }
    setFogRoll(fogRollState());
    releaseBurstPrewarm(this._warmBursts);
    this._warmBursts.length = 0;
    // The arena stays unculled until the last warm frame has been drawn.
    if (!this._warmQueue?.length) this._recull();
  }

  _recull() {
    for (const mesh of this._warmUnculled) mesh.frustumCulled = true;
    this._warmUnculled.length = 0;
  }

  /** One overlay kind per frame, drawn for real at no visible size. */
  _warmDraw(mats) {
    if (!this._warmQueue?.length) {
      this._warmQueue = null;
      return;
    }
    // Not before every pending pipeline has landed (see `pipelinesSettled`).
    if (!this._warmActive) {
      const now = performance.now();
      this._warmWaitSince ??= now;
      if (!pipelinesSettled(this._renderer) && now - this._warmWaitSince < WARM_WAIT_MS) return;
      this._warmWaitSince = null;
    }
    const kind = this._warmQueue.shift();
    const first = !this._warmActive;
    this._warmActive = true;
    for (const mesh of [this._chaosGround, ...this._chaosQuads]) {
      if (!mesh) continue;
      mesh.material = mats[kind];
      mesh.visible = true;
      mesh.scale.setScalar(WARM_SCALE);
    }
    if (!first) return;
    for (const cloud of fogCloudMeshes()) {
      cloud.userData.warmScale = cloud.scale.x;
      cloud.visible = true;
      cloud.scale.setScalar(WARM_SCALE);
    }
    // Every arena mesh through the pass once, in view or not. The Exchange
    // and HFT Floor are hundreds of per-material meshes, and at `high` the
    // outline pass builds one node state per mesh the first time the camera
    // sees it — 1–2.6 ms each, twenty at a time when a shake moves the frame
    // edge, which is what turned a lava landing into a 54 ms frame there.
    this.root.traverse((o) => {
      if (o.isMesh && o.frustumCulled) {
        o.frustumCulled = false;
        this._warmUnculled.push(o);
      }
    });
    // Taken OUT of the burst pool for these frames, so a burst spawned
    // meanwhile cannot be handed the mesh the restore is about to remove.
    for (const res of acquireBurstPrewarm()) {
      setParticleFade(res.mesh.material, 0);
      this.scene.add(res.mesh);
      this._warmBursts.push(res);
    }
  }

  /** Which kind shows where, the conveyor's heading, the wipe and the erosion. */
  _applyFloor(f, mats) {

    if (f.mutation?.kind === 'conveyor_floor') {
      // Same heading convention as the sim (sin, cos), so the chevrons point
      // exactly where the floor pushes.
      const rad = (f.mutation.directionDeg * Math.PI) / 180;
      mats.conveyorDir.value.set(Math.sin(rad), Math.cos(rad));
      mats.conveyorSpeed.value = f.mutation.driftSpeed;
    }

    if (f.visible) {
      // Material swaps only on kind changes.
      if (this._chaosKind !== f.kind) {
        this._chaosKind = f.kind;
        if (this._chaosGround) this._chaosGround.material = mats[f.kind];
      }
      if (f.raised) for (const quad of this._chaosQuads) if (quad.material !== mats[f.raised]) quad.material = mats[f.raised];
    }
    // Two float writes a frame, unconditionally, so the uniforms never hold a
    // value from the frame before the overlay hid.
    mats.sweep.value = 0.05 + easeOut(f.sweep) * this._overlayRadius * 1.3;
    mats.erode.value = overlayErodeValue(f.erode);
    if (this._chaosGround) this._chaosGround.visible = f.visible;
    const quadsVisible = f.visible && f.quads;
    for (const quad of this._chaosQuads) quad.visible = quadsVisible;
  }

  _buildWindZone(zone) {
    // Volume marker only. The wind itself is applied by the sim on both sides;
    // this is here so a player can see why they are being pushed.
    const geo = new THREE.BoxGeometry(zone.hx * 2, zone.hy * 2, zone.hz * 2);
    const mat = new THREE.MeshBasicNodeMaterial({
      color: 0x9fd0ff,
      transparent: true,
      opacity: 0.08,
      depthWrite: false,
    });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.set(zone.x, zone.y, zone.z);
    mesh.renderOrder = -2;
    mesh.userData.ownsMaterial = true;
    return mesh;
  }

  clear() {
    this._localVisualController?.dispose?.();
    this._localVisualController = null;
    this.ready = Promise.resolve();
    this.assetState = 'none';
    this._assetRoot = null;
    this._assetStatus?.remove();
    this._assetStatus = null;
    clearProps(this.root);
    clearAllBatches();
    clearConveyorMaterials();
    clearPlatformRimMaterials();
    for (let i = this.root.children.length - 1; i >= 0; i--) {
      const child = this.root.children[i];
      this.root.remove(child);
      // Geometry is per-mesh and always safe to drop. Materials are only safe
      // when this mesh built its own: the shared surface materials live in
      // ToonMaterials' cache, and disposing one here would leave every other
      // platform of that kind pointing at a dead pipeline.
      // `traverse` rather than a direct dispose: a prop is a Group whose
      // geometry hangs off its children, so disposing the Group freed nothing
      // and every arena rebuild leaked a desk's worth of buffers.
      child.traverse((o) => {
        o.geometry?.dispose();
        if (o !== child && o.userData.ownsMaterial) o.material?.dispose();
      });
      if (child.userData.ownsMaterial) {
        // material.dispose() does not free textures — the per-conveyor cloned
        // scroll texture leaked one GPU texture per conveyor per match.
        if (child.userData.ownsMap) child.material?.map?.dispose();
        child.material?.dispose();
      }
    }
    // Every mesh referencing the cached surface materials is gone as of the
    // loop above, so the per-theme cache can be dropped too; without this,
    // every theme visited in a session left its materials on the GPU forever.
    clearMaterialCache();
    this._floorMesh = null;
    this._chaosGround = null;
    this._chaosQuads = [];
    this._chaosKind = null;
    // A warm draw in flight belongs to the arena that just went away.
    this._warmQueue = null;
    this._warmActive = false;
    this._warmWaitSince = null;
    this._warmUnculled.length = 0;
    releaseBurstPrewarm(this._warmBursts);
    this._warmBursts.length = 0;
  }
}
